import { useState, useEffect, useRef } from 'react';
import YoutubePlayer from './youtube';
import './main.css';

let buzzer = null;

function Main() {
    const STATES = {
        STOP: 0,
        MAIN_START: 1,
        MAIN_PAUSED: 2,
        MAIN_RESTART: 3,
        INTERVAL_START: 4,
        INTERVAL_PAUSED: 5,
        INTERVAL_RESTART: 6,
    };

    const BUZZER_SECOND = 3000;
    const TIME_COUNT = localStorage.getItem('timeCount') || 20;
    const INTERVAL_COUNT = localStorage.getItem('intervalCount') || 10;
    const [timerCount, setTimerCount] = useState({ second: TIME_COUNT, progress: 0, inputSecond: TIME_COUNT });
    const [intervalCount, setIntervalCount] = useState({ second: INTERVAL_COUNT, progress: 0, inputSecond: INTERVAL_COUNT });
    const [state, setState] = useState(STATES.STOP);
    const isPlayBuzzerRef = useRef(false);
    const timerCountMainRef = useRef(null);
    const intervalCountRef = useRef(null);
    const initialMemo = useRef(localStorage.getItem('memo') || '');
    const buttonStartRef = useRef(null);
    const buttonStopRef = useRef(null);
    const buttonResetRef = useRef(null);
    const rootStyle = document.documentElement.style;
    const timerID = useRef(null);
    let startCount = useRef(0);
    let progressCount = useRef(0);

    useEffect(() => {
        switch(state) {
            case STATES.STOP:
                clearTimeout(timerID.current);
                buttonStartRef.current.disabled = false;
                buttonStopRef.current.disabled = true;
                buttonResetRef.current.disabled = true;
                stopBuzzer();
                break;
            case STATES.MAIN_START:
                resetTimer();
                rootStyle.setProperty('--progress', '0%');
                timerCountMainRef.current.classList.add('active');
                intervalCountRef.current.classList.remove('active');
                buttonStartRef.current.disabled = true;
                buttonStopRef.current.disabled = false;
                buttonResetRef.current.disabled = false;
                isPlayBuzzerRef.current = false;
                startTimer();
                break;
            case STATES.MAIN_RESTART:
                buttonStartRef.current.disabled = true;
                buttonStopRef.current.disabled = false;
                buttonResetRef.current.disabled = false;
                startTimer();
                break;
            case STATES.MAIN_PAUSED:
            case STATES.INTERVAL_PAUSED:
                clearTimeout(timerID.current);
                buttonStartRef.current.disabled = false;
                buttonStopRef.current.disabled = true;
                buttonResetRef.current.disabled = false;
                stopBuzzer();
                break;
            case STATES.INTERVAL_START:
                resetTimer();
                rootStyle.setProperty('--progress', '0%');
                timerCountMainRef.current.classList.remove('active');
                intervalCountRef.current.classList.add('active');
                buttonStartRef.current.disabled = true;
                buttonStopRef.current.disabled = false;
                buttonResetRef.current.disabled = false;
                isPlayBuzzerRef.current = false;
                startInterval();
                break;
            case STATES.INTERVAL_RESTART:
                buttonStartRef.current.disabled = true;
                buttonStopRef.current.disabled = false;
                buttonResetRef.current.disabled = false;
                startInterval();
                break;
            default:
                // dummy
        }
    }, [state]);

    const startTimer = () => {
        startCount = new Date().getTime();
        progressCount = timerCount.progress;
        timerID.current = setTimeout(updateRunningTimer, 10);

        function updateRunningTimer() {
            const count = progressCount + new Date().getTime() - startCount;
            const duration = timerCount.inputSecond * 1000;
            const second = (timerCount.inputSecond) - Math.floor(count / 1000);
            const progress = (count / duration) * 100;
            const formattedProgress = progress + '%';
            rootStyle.setProperty('--progress', formattedProgress);

            setTimerCount((prevTimerCount) => {
                let newTimerCount = { ...prevTimerCount };
                newTimerCount.progress = count;
                newTimerCount.second = second;
                return newTimerCount;
            });

            if (count >= (duration - BUZZER_SECOND) && !isPlayBuzzerRef.current) {
                playBuzzer();
            }

            if (count >= duration) {
                clearTimeout(timerID.current);
                setState(STATES.INTERVAL_START);
            }
            else {
                timerID.current = setTimeout(updateRunningTimer, 10);
            }
        }
    };

    const startInterval = () => {
        startCount = new Date().getTime();
        progressCount = intervalCount.progress;
        timerID.current = setTimeout(updateIntervalTimer, 10);

        function updateIntervalTimer() {
            const count = progressCount + new Date().getTime() - startCount;
            const duration = intervalCount.inputSecond * 1000;
            const second = (intervalCount.inputSecond) - Math.floor(count / 1000);
            const progress = (count / duration) * 100;
            const formattedProgress = progress + '%';
            rootStyle.setProperty('--progress', formattedProgress);

            setIntervalCount((prevIntervalCount) => {
                let newIntervalCount = { ...prevIntervalCount };
                newIntervalCount.progress = count;
                newIntervalCount.second = second;
                return newIntervalCount;
            });

            if (count >= (duration - BUZZER_SECOND) && !isPlayBuzzerRef.current) {
                playBuzzer();
            }

            if (count >= duration) {
                clearTimeout(timerID.current);
                setState(STATES.MAIN_START);
            }
            else {
                timerID.current = setTimeout(updateIntervalTimer, 10);
            }
        }
    };

    const playBuzzer = () => {
        if(buzzer) {
            isPlayBuzzerRef.current = true;
            buzzer.play();
        }
    };

    const stopBuzzer = () => {
        if(buzzer) {
            buzzer.pause();
            buzzer.currentTime = 0;
        }
    };

    const resetTimer = () => {
        rootStyle.setProperty('--progress', '0%');

        setTimerCount((prevTimerCount) => ({
            ...prevTimerCount,
            inputSecond: TIME_COUNT,
            progress: 0,
            second: TIME_COUNT,
        }));
        
        setIntervalCount((prevIntervalCount) => ({
            ...prevIntervalCount,
            inputSecond: INTERVAL_COUNT,
            progress: 0,
            second: INTERVAL_COUNT,
        }));
    };

    const handleStart = () => {
        // モバイルで音声が鳴らない問題の対策。操作がされたときにAudioを生成する
        if(!buzzer) {
            buzzer = new Audio(process.env.PUBLIC_URL + '/buzzer.mp3');
            buzzer.pause();
            buzzer.currentTime = 0;
            buzzer.preload = 'auto';
        }

        switch(state) {
            case STATES.STOP:
                setState(STATES.MAIN_START);
                break;
            case STATES.MAIN_PAUSED:
                setState(STATES.MAIN_RESTART);
                break;
            case STATES.INTERVAL_PAUSED:
                setState(STATES.INTERVAL_RESTART);
                break;
            default:
                // dummy
        }
    };

    const handleStop = () => {
        switch(state) {
            case STATES.MAIN_START:
            case STATES.MAIN_RESTART:
                setState(STATES.MAIN_PAUSED);
                break;
            case STATES.INTERVAL_START:
            case STATES.INTERVAL_RESTART:
                setState(STATES.INTERVAL_PAUSED);
                break;
            default:
                // dummy
        }
    };

    const handleReset = () => {
        resetTimer();
        timerCountMainRef.current.classList.remove('active');
        intervalCountRef.current.classList.remove('active');
        setState(STATES.STOP);
    };

    const handleChangeTimerCount = (event) => {
        const number = convertNumber(event.target.value);
        let value = parseInt(number);
        if(isNaN(value)) {
            value = 0;
        }
        setTimerCount((prevTimerCount) => ({
            ...prevTimerCount,
            inputSecond: value,
            second: value,
        }));
        localStorage.setItem('timeCount', value);
    };

    const handleChangeIntervalCount = (event) => {
        const number = convertNumber(event.target.value);
        let value = parseInt(number);
        if(isNaN(value)) {
            value = 0;
        }
        setIntervalCount((prevIntervalCount) => ({
            ...prevIntervalCount,
            inputSecond: value,
            second: value,
        }));
        localStorage.setItem('intervalCount', value);
    };

    const handleChangeMemo = (event) => {
        localStorage.setItem('memo', event.target.value);
    };

    function convertNumber(number) {
        const onlyNumber = number.replace(/[^0-9０-９]/g, '');
        const result = onlyNumber.replace(/[０-９]/g, function(s) {
            return String.fromCharCode(s.charCodeAt(0) - 0xFEE0);
        });

        return result;
    }

    const toOutputString = (num) => {
        return String(num).padStart(2, '0');
    };

    return (
        <div className="container">
            <main className="main">
                <section className="section_timer">
                    <div className="timer">
                        <div className="timer_count">
                            <div className="timer_count_main" ref={timerCountMainRef}>
                                <input type="tel" min="1" value={toOutputString(timerCount.second)} onChange={handleChangeTimerCount}></input>
                            </div>
                            <div className="timer_count_interval" ref={intervalCountRef}>
                                <input type="tel" min="1" value={toOutputString(intervalCount.second)} onChange={handleChangeIntervalCount}></input>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="section_console btn_blue">
                    <button className="btn_start" onClick={handleStart} ref={buttonStartRef}>スタート</button>
                    <button className="btn_stop" onClick={handleStop} ref={buttonStopRef}>ストップ</button>
                    <button className="btn_reset" onClick={handleReset} ref={buttonResetRef}>リセット</button>
                </section>

                <section className="section_textarea">
                    <div className="section_textarea_title">メモ欄</div>
                    <textarea rows="8" placeholder="トレーニングメニューや記録の入力などに活用ください。入力内容はブラウザに保存されます。" defaultValue={initialMemo.current} onChange={handleChangeMemo}></textarea>
                </section>

                <section className="section_iframe">
                    <div className="section_iframe_title">YouTube</div>
                    <YoutubePlayer></YoutubePlayer>
                </section>
            </main>
        </div>
    );
}

export default Main;
